/* eslint-disable no-debugger */
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import Cookies from "js-cookie";
import { notification, Spin, Empty, Tooltip } from "antd";
import moment from "moment";
import {
  HiOutlineSearch,
  HiOutlineCalendar,
  HiOutlineClock,
  HiOutlineUser,
  HiOutlineCheckCircle,
  HiOutlinePlus,
  HiOutlinePhone,
  HiOutlineEye
} from 'react-icons/hi';

const AppointmentsMaster = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [filterDate, setFilterDate] = useState(moment().format('YYYY-MM-DD'));
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState({ total: 0, completed: 0, pending: 0 });

  useEffect(() => {
    fetchAppointments();
  }, [filterDate]);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const doctorId = Cookies.get("Doctor_id");
      const clinicId = Cookies.get("Clinic_id");

      const response = await axiosInstance.post("doctor/getAppointmentList", {
        doctor_id: doctorId,
        clinic_id: clinicId,
        search_date: filterDate,
        search_date_to: filterDate,
        period: "day",
        patient_type: ""
      });

      if (response.data.status === 1) {
        const data = response.data.data[0]?.result || [];
        setAppointments(data);

        const completed = data.filter(a => a.check_out_status === 1).length;
        setStats({
          total: data.length,
          completed: completed,
          pending: data.length - completed
        });
      } else {
        setAppointments([]);
        setStats({ total: 0, completed: 0, pending: 0 });
      }
    } catch (err) {
      console.error("Failed to fetch appointments", err);
      notification.error({ message: "Failed to load appointments" });
    } finally {
      setLoading(false);
    }
  };

  const filteredAppointments = appointments.filter(app =>
    (app.patientName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (app.patientMobile || "").includes(searchTerm)
  );

  const getStatusTag = (app) => {
    if (app.check_out_status === 1) {
      return (
        <span className="inline-block bg-emerald-50 text-emerald-600 border border-emerald-300 text-xs font-semibold px-2.5 py-0.5 rounded-md whitespace-nowrap">
          Completed
        </span>
      );
    }
    if (app.check_in_status === 1 && app.arrived_status === "1") {
      return (
        <span className="inline-block bg-teal-50 text-teal-700 border border-teal-200 text-xs font-semibold px-2.5 py-0.5 rounded-md whitespace-nowrap">
          In Consultation
        </span>
      );
    }
    if (app.arrived_status === "1" && app.check_in_status === 0) {
      return (
        <span className="inline-block bg-amber-50 text-amber-700 border border-amber-200 text-xs font-semibold px-2.5 py-0.5 rounded-md whitespace-nowrap">
          Arrived
        </span>
      );
    }
    return (
      <span className="inline-block bg-gray-50 text-gray-600 border border-gray-200 text-xs font-semibold px-2.5 py-0.5 rounded-md whitespace-nowrap">
        Scheduled
      </span>
    );
  };

  const check = async (bookid, walkin, name, appointment_date) => {
    setLoading(true);
    try {
      const currentDate = moment();
      const formattedTime = currentDate.format("HH:mm:ss");
      const today = currentDate.format("YYYY-MM-DD");

      if (today === appointment_date) {
        const response = await axiosInstance.put("doctor/updateCheckedIn", {
          book_id: bookid,
          is_walkin: walkin,
          checked_in: formattedTime,
          arrived_status: 1,
          available_status: "checkin"
        });

        if (response.data.status === 1) {
          notification.success({
            message: "Success",
            description: "Member " + name + " checked-in successfully.",
          });
          fetchAppointments();
        } else {
          notification.error({ message: response.data.msg || "Check-in failed" });
        }
      } else {
        notification.error({ message: "Check-in is only allowed for today's appointments" });
      }
    } catch (error) {
      console.error("Check-in error", error);
      notification.error({ message: "Something went wrong during check-in" });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (bookingId) => {
    const today = moment().format('YYYY-MM-DD');
    if (filterDate !== today) {
      notification.error({
        message: "Access Denied",
        description: "Appointment details are only available for today's list.",
        duration: 4,
        style: {
          borderRadius: '12px',
          border: '1px solid #fee2e2',
          backgroundColor: '#fef2f2'
        }
      });
      return;
    }
    history.push(`/dashboard/appointments/details/${bookingId}`);
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-300 w-full text-gray-800">
      {/* Header Card: Responsively Stacked on Mobile, Inline on Desktop */}
      <div className="bg-white px-3.5 sm:px-5 py-3 rounded-xl border border-gray-200 shadow-2xs flex flex-col sm:flex-row items-start sm:items-center justify-between w-full box-border gap-3 sm:gap-0">
        <div className="text-left shrink-0">
          <h2 className="text-sm sm:text-base font-bold text-gray-800 leading-tight m-0">Appointments</h2>
          <p className="text-gray-400 text-[11px] sm:text-xs m-0 mt-0.5 font-medium">Manage and track all patient visits</p>
        </div>
        <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 shrink-0 w-full sm:w-auto">
          <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 flex-1 sm:flex-none">
            <HiOutlineCalendar className="text-gray-400 shrink-0" size={16} />
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="bg-transparent border-none text-xs font-semibold text-gray-700 outline-none cursor-pointer w-full"
            />
          </div>
          <button
            onClick={() => history.push('/dashboard/walkinbooking')}
            className="flex items-center justify-center gap-1.5 bg-[#009688] hover:bg-[#00897b] text-white px-3.5 py-2 rounded-lg font-bold text-xs transition-all shadow-2xs border-none cursor-pointer whitespace-nowrap w-full sm:w-auto"
          >
            <HiOutlinePlus size={16} />
            <span>Walk-in Booking</span>
          </button>
        </div>
      </div>

      {/* 3 Stats Cards - Small/Medium Size in Horizontal Left-to-Right Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full">
        {/* Total Visits */}
        <div className="bg-white px-4 py-3 rounded-xl border border-gray-200 shadow-2xs flex items-center justify-between min-w-0">
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider m-0">Total Visits</p>
            <p className="text-lg font-bold text-blue-600 leading-tight mt-1 m-0">{stats.total}</p>
          </div>
          <div className="w-8 h-8 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0">
            <HiOutlineUser size={16} />
          </div>
        </div>

        {/* Completed */}
        <div className="bg-white px-4 py-3 rounded-xl border border-gray-200 shadow-2xs flex items-center justify-between min-w-0">
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider m-0">Completed</p>
            <p className="text-lg font-bold text-emerald-600 leading-tight mt-1 m-0">{stats.completed}</p>
          </div>
          <div className="w-8 h-8 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
            <HiOutlineCheckCircle size={16} />
          </div>
        </div>

        {/* Pending */}
        <div className="bg-white px-4 py-3 rounded-xl border border-gray-200 shadow-2xs flex items-center justify-between min-w-0">
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider m-0">Pending</p>
            <p className="text-lg font-bold text-amber-500 leading-tight mt-1 m-0">{stats.pending}</p>
          </div>
          <div className="w-8 h-8 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-500 shrink-0">
            <HiOutlineClock size={16} />
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white px-4 py-2.5 rounded-xl border border-gray-200 shadow-2xs flex items-center">
        <div className="relative flex-1 w-full">
          <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Search by patient name or mobile number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs outline-none focus:border-teal-500 transition-all font-medium h-9"
          />
        </div>
      </div>

      {/* Appointments Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-2xs overflow-hidden">
        {loading ? (
          <div className="py-16 flex flex-col items-center justify-center gap-2">
            <Spin size="small" />
            <p className="text-gray-400 font-medium text-xs m-0">Updating list...</p>
          </div>
        ) : filteredAppointments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-left border-collapse">
              <thead className="bg-slate-50 border-b border-slate-100 text-gray-700 text-[11px] uppercase font-bold tracking-wider">
                <tr>
                  <th className="px-4 py-3">Patient Details</th>
                  <th className="px-4 py-3">Time & Service</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actions</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3 text-right">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filteredAppointments.map((app, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/70 transition-colors group">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-xs shrink-0">
                          {app.patientName?.charAt(0) || app.name?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-bold text-gray-800 text-xs m-0">{app.name || app.patientName}</p>
                          <div className="flex items-center gap-1 text-gray-400 text-[11px] mt-0.5">
                            <HiOutlinePhone size={12} />
                            <span>{app.mobile_no || app.patientMobile}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1 text-gray-800 font-semibold text-xs">
                          <HiOutlineClock className="text-teal-600" size={14} />
                          <span>{app.appointment_time ? moment(app.appointment_time, "HH:mm:ss").format("hh:mm A") : "N/A"}</span>
                        </div>
                        <span className="text-[11px] text-gray-400 font-medium mt-0.5">{app.service_type || 'General Clinic'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusTag(app)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        disabled={app.check_in_status === 1}
                        onClick={() => {
                          if (app.check_in_status !== 1) {
                            check(
                              app.booking_id,
                              app.appointment_type_id == 0 ? 1 : 0,
                              app.patientName || app.name,
                              app.appointment_date
                            );
                          }
                        }}
                        className={`px-3 py-1 rounded-md text-xs font-bold transition-all border-none cursor-pointer ${
                          app.check_in_status === 1
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-[#009688] text-white hover:bg-[#00897b] shadow-2xs"
                        }`}
                      >
                        {app.check_in_status === 1 ? "Checked In" : "Check In"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600 font-medium">{app.appointment_type || 'General Clinic'}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Tooltip title="View Details">
                        <button
                          onClick={() => handleViewDetails(app.booking_id)}
                          className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all border-none bg-transparent cursor-pointer"
                        >
                          <HiOutlineEye size={18} />
                        </button>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-20 text-center">
            <Empty description={<span className="text-gray-400 font-medium text-xs">No appointments found for this date</span>} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentsMaster;
